import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";

import Autocomplete from "react-google-autocomplete";
import { Map, GoogleApiWrapper, Marker } from 'google-maps-react'; //https://www.digitalocean.com/community/tutorials/how-to-integrate-the-google-maps-api-into-react-applications

import { useRecoilState } from "recoil";
import { accountState } from "../../Shared/atom";

import Input from "../common/Input.Component";

import emailHelper from "../../Shared/emailHelper";
import message from "../../Shared/message";
import phoneHelper from "../../Shared/phoneHelper";

import styles from "./RequestService.module.scss";

const RequestService = ({ isOpen, onSendClick, onCancelClick, google }) => {
    // -- Global State ---------------------------------------------------------------------------------------------------------------------
    const [portalAccount] = useRecoilState(accountState);

    // -- Page State -----------------------------------------------------------------------------------------------------------------------
    const [contact, contactSet] = useState({ firstName: null, lastName: null, email: null, phone: null, address: null, siteAddress: {}, serviceDescription: null, errors: {} });
    const [mapCenter, mapCenterSet] = useState();
    const [setContact, setContactSet] = useState(true);

    // -- State Events --------------------------------------------------------------------------------------------------------------------
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (setContact) {
            let _contact = { ...contact };

            _contact.firstName = portalAccount?.siteContact?.firstName;
            _contact.lastName = portalAccount?.siteContact?.lastName;
            _contact.email = portalAccount?.siteContact?.email;
            _contact.phone = phoneHelper.format(portalAccount?.siteContact?.preferredNumber);

            _contact.siteAddress.line1 = portalAccount?.siteAddress?.line1;
            _contact.siteAddress.line2 = portalAccount?.siteAddress?.line2;
            _contact.siteAddress.suburb = portalAccount?.siteAddress?.suburb;
            _contact.siteAddress.state = portalAccount?.siteAddress?.state;
            _contact.siteAddress.postcode = portalAccount?.siteAddress?.postcode;
            _contact.siteAddress.country = portalAccount?.siteAddress?.country;
            _contact.siteAddress.latitude = portalAccount?.siteAddress?.latitude ? portalAccount.siteAddress.latitude : null;
            _contact.siteAddress.longitude = portalAccount?.siteAddress?.longitude ? portalAccount.siteAddress.longitude : null;
            _contact.address = formatDisplayAddress(_contact.siteAddress);
            _contact.errors = {};

            setContactSet(false);
            contactSet(_contact);
        }
    });

    // -- Form Methods --------------------------------------------------------------------------------------------------------------------
    const getErrorAlert = (error) => {
        return <div className="alert alert-danger alert-error" role="alert" style={{ fontStyle: "italic", fontSize: "0.85rem", marginTop: "-5px", zIndex: "10" }}>{error}</div>
    };

    const handleCancelClick = () => {
        setContactSet(true);
        onCancelClick();
    };

    const isFieldValid = (id, value, errors) => {
        errors[id] = null;

        switch (id) {
            case "firstName":
            case "lastName":
                if (value.length < 2)
                    errors[id] = "The name must contain at least 2 characters.";

                break;

            case "email":
                if (!value)
                    errors[id] = "This is a required field."

                else if (value && !emailHelper.isValidEmail(value))
                    errors[id] = "This does not appear to be a valid email."

                break;

            case "phone":
                if (!value)
                    errors[id] = "This is a required field."

                else if (value && !phoneHelper.isValid(value))
                    errors[id] = "This does not appear to be a valid phone number."

                break;

            case "serviceDescription":
                if (!value)
                    errors[id] = "This is a required field."

                break;

            //no default
        }

        return errors[id] === null;
    };

    const handleOnBlur = (evt) => {
        const tgt = evt.target;
        const value = tgt.value.trim();
        const controlIds = tgt.id.split(".");

        let _contact = { ...contact };
        let _errors = { ..._contact.errors };

        if (controlIds.length === 1) {

            if (isFieldValid(controlIds[0], value, _errors)) {
                // Some controls may need further processing...
                switch (controlIds[0]) {
                    case "phone":
                        _contact.phone = phoneHelper.format(_contact.phone);
                        break;

                    //no default
                }
            }
        }

        _contact.errors = _errors;
        contactSet(_contact);
    };

    const handleOnChange = (evt) => {
        const tgt = evt.target;
        const value = tgt.value;
        const controlIds = tgt.id.split(".");

        let _contact = { ...contact };

        if (controlIds.length === 1) {
            _contact[controlIds[0]] = value;
        }

        else {
            _contact[controlIds[0]][controlIds[1]] = value;
        }

        contactSet(_contact);
    };

    const handleOnFocus = (evt) => evt.target.select();

    const formatDisplayAddress = (_siteAddress) => {
        let _address = "";

        try {
            if (_siteAddress?.line2)
                _address = `${_siteAddress.line2.trim()}\n`;

            if (_siteAddress?.line1)
                _address = `${_address}${_siteAddress.line1.trim()}\n`;

            _address = `${_address}${_siteAddress.suburb ? `${_siteAddress.suburb.trim()} ` : ""}${_siteAddress.state ? `${_siteAddress.state.trim()} ` : ""}${_siteAddress.postcode ? `${_siteAddress.postcode.trim()}` : ""}\n`
            _address = `${_siteAddress.country ? `${_address}${_siteAddress.country.trim()}` : _address}`;

            // Remove any leading or trailing CrLfs...
            _address = _address.replace(/^\n|\n$/g, '');
        }

        catch (ex) {
            message.Warning("Site Address", "There was an error formatting the address data.\n\nPlease try again and if the problem persists contact 1300&nbsp;SHREDX (1300&nbsp;747&#8209;339) for assistance.");
        }

        return _address;
    }

    const handleSubmitClick = () => {
        if (!isFormValid()) return;

        const serviceDescription = {
            contactFirstName: contact.firstName,
            contactLastName: contact.lastName,
            serviceAddress: contact.siteAddress,
            contactEmail: contact.email,
            contactPhone: contact.phone,
            serviceDescription: contact.serviceDescription,
            submittedBy: portalAccount?.siteContact?.name ?? "Unknown",
            submittedByEmail: portalAccount.siteContact?.email ?? "",
            submittedDt: new Date()
        };

        onSendClick(serviceDescription);
    }

    const isFormValid = () => {
        let hasErrors = false;
        let _contact = { ...contact };
        let _errors = { ..._contact.errors };

        hasErrors = !isFieldValid("firstName", contact.firstName, _errors);
        hasErrors = !isFieldValid("lastName", contact.lastName, _errors) || hasErrors;
        hasErrors = !isFieldValid("email", contact.email, _errors) || hasErrors;
        hasErrors = !isFieldValid("phone", contact.phone, _errors) || hasErrors;
        hasErrors = !isFieldValid("serviceDescription", contact.serviceDescription, _errors) || hasErrors;

        if (hasErrors) {
            _contact.errors = _errors;
            contactSet(_contact);
            message.Warning("Send Service Request", "Please correct the form errors before submitting the service request.");
        }

        return !hasErrors;
    }

    // -- Render --------------------------------------------------------------------------------------------------------------------------
    // const sitePhoneError = (phone && phoneHasError)
    //     ? "block"
    //     : "none";

    const firstNameError = contact?.errors?.firstName
        ? getErrorAlert(contact.errors.firstName)
        : null;

    const lastNameError = contact?.errors?.lastName
        ? getErrorAlert(contact.errors.lastName)
        : null;

    const emailError = contact?.errors?.email
        ? getErrorAlert(contact.errors.email)
        : null;

    const phoneError = contact?.errors?.phone
        ? getErrorAlert(contact.errors.phone)
        : null;

    const serviceDescriptionError = contact?.errors?.serviceDescription
        ? getErrorAlert(contact.errors.serviceDescription)
        : null;

    // The Input component was wrapping the "<input...>" element with "<div class='mb-3'>" which was screwing up the layout of the form.
    // Consequently normal input controls are used to display the form.
    return (
        <Modal backdrop="static" className={`form-control ${styles["modal-width"]}`} isOpen={isOpen} toggle={handleCancelClick}>
            <ModalHeader>Request a service</ModalHeader>

            <ModalBody>

                <div className={styles["request-service"]}>
                    <div className={styles["data"]}>

                        <div className={`${styles["firstName"]} ${styles["data-row"]}`}>
                            <label className="col-form-label mandatory" htmlFor="firstName">First Name:</label>

                            <div className={styles["data-input"]}>
                                <input
                                    id="firstName"
                                    placeholder="Contact's first/given name"
                                    className="form-control"
                                    value={contact.firstName}
                                    onFocus={(evt) => handleOnFocus(evt)}
                                    onChange={(evt) => handleOnChange(evt)}
                                    onBlur={(evt) => handleOnBlur(evt)}
                                />

                                {firstNameError}
                            </div>
                        </div>

                        <div className={`${styles["lastName"]} ${styles["data-row"]}`}>
                            <label className="col-form-label mandatory" htmlFor="lastName">Last Name:</label>

                            <div className={styles["data-input"]}>
                                <input
                                    id="lastName"
                                    placeholder="Contact's last/family name"
                                    className="form-control"
                                    value={contact.lastName}
                                    onFocus={(evt) => handleOnFocus(evt)}
                                    onChange={(evt) => handleOnChange(evt)}
                                    onBlur={(evt) => handleOnBlur(evt)}
                                />

                                {lastNameError}
                            </div>
                        </div>

                        <div className={`${styles["email"]} ${styles["data-row"]}`}>
                            <label className="col-form-label mandatory" htmlFor="email">Email:</label>

                            <div className={styles["data-input"]}>
                                <input
                                    id="email"
                                    className="form-control"
                                    placeholder="Contact's email address"
                                    value={contact.email}
                                    onFocus={(evt) => handleOnFocus(evt)}
                                    onChange={(evt) => handleOnChange(evt)}
                                    onBlur={(evt) => handleOnBlur(evt)}
                                />

                                {emailError}
                            </div>
                        </div>

                        <div className={`${styles["phone"]} ${styles["data-row"]}`}>
                            <label className="col-form-label mandatory" htmlFor="phone">Phone:</label>

                            <div className={styles["data-input"]}>
                                <input
                                    id="phone"
                                    type="tel"
                                    className={`form-control ${styles["data-input"]}`}
                                    placeholder="Contact's preferred phone number"
                                    value={contact.phone}
                                    onFocus={(evt) => handleOnFocus(evt)}
                                    onChange={(evt) => handleOnChange(evt)}
                                    onBlur={(evt) => handleOnBlur(evt)}
                                />

                                {phoneError}
                            </div>
                        </div>

                        <div className={styles["addressDetail"]}>
                            <div className={styles["data-row"]}>
                                <label className="col-form-label" htmlFor="line2">Address Detail:</label>

                                <input
                                    id="siteAddress.line2"
                                    className={`form-control ${styles["data-input"]}`}
                                    placeholder='E.g. Unit 101" or "Level 1" or "Arundel Plaza" etc...'
                                    value={contact.siteAddress.line2}
                                    onFocus={(evt) => handleOnFocus(evt)}
                                    onChange={(evt) => handleOnChange(evt)}
                                    onBlur={(evt) => handleOnBlur(evt)}
                                />
                            </div>
                        </div>

                        <div className={`${styles["address"]} ${styles["data-row"]}`}>
                            <label className="col-form-label mandatory">Address:</label>

                            <Autocomplete
                                id="address"
                                placeholder="Site address"
                                className={`form-control ${styles["data-input"]}`}
                                apiKey={process.env.REACT_APP_GOOGLE_AUTOCOMPLETE_KEY}
                                options={{ componentRestrictions: { country: ["au", "nz"] }, types: ["address"] }}

                                onPlaceSelected={(place) => {
                                    let streetNo, streetName, suburb, state, postcode, country;

                                    for (let i = 0; i < place.address_components.length; i++) {
                                        switch (place.address_components[i].types[0]) {
                                            case "street_number":
                                                streetNo = place.address_components[i].long_name + "";
                                                break;

                                            case "route":
                                                streetName = place.address_components[i].long_name;
                                                break;

                                            case "locality":
                                            case "administrative_area_level_3":
                                                suburb = place.address_components[i].long_name;
                                                break;

                                            case "postal_code":
                                                postcode = place.address_components[i].long_name;
                                                break;

                                            case "administrative_area_level_1":
                                                state = place.address_components[i].short_name;
                                                break;

                                            case "country":
                                                country = place.address_components[i].long_name;
                                                break;

                                            default:
                                        }
                                    }

                                    let _contact = { ...contact };

                                    _contact.siteAddress.line1 = ((streetNo || "") + " " + (streetName || "")).trim();
                                    _contact.siteAddress.suburb = suburb;
                                    _contact.siteAddress.state = state;
                                    _contact.siteAddress.postcode = postcode;
                                    _contact.siteAddress.country = country;
                                    _contact.siteAddress.latitude = place.geometry.location.lat();
                                    _contact.siteAddress.longitude = place.geometry.location.lng();
                                    _contact.address = formatDisplayAddress(_contact.siteAddress);

                                    mapCenterSet(place.geometry.location);
                                    contactSet(_contact);
                                }}
                            />
                        </div>

                        <div className={`${styles["addressPanel"]} ${styles["data-row"]}`}>
                            <textarea
                                id="addressPanel"
                                rows="5"
                                className={`form-control ${styles["data-input"]}`}
                                readOnly={true}
                                value={contact.address}
                            />
                        </div>

                        <div className={styles["request"]}>
                            <Input
                                id="serviceDescription"
                                type="textarea"
                                placeholder="Please provide a description of the required service"
                                rows="5"
                                mandatory={true}
                                value={contact.serviceDescription}
                                onFocus={(evt) => handleOnFocus(evt)}
                                onChange={(evt) => handleOnChange(evt)}
                                onBlur={(evt) => handleOnBlur(evt)}
                            />

                            {serviceDescriptionError}
                        </div>
                    </div>

                    <div className={styles["map"]}>
                        {!(contact?.siteAddress && contact.siteAddress.latitude && contact.siteAddress.longitude) && (
                            <div className="d-flex flex-column justify-content-center align-items-center h-100 p-4">
                                <img
                                    src="data:image/png;base64,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"
                                    width="110"
                                    height="100"
                                    alt="File:Google Maps Logo 2020.svg - Wikimedia Commons" />

                                <div className="text-center mt-4" style={{ color: "#e54800" }}>Location data is missing for this address</div>
                            </div>
                        )}

                        {contact?.siteAddress && contact.siteAddress.latitude && contact.siteAddress.longitude && (
                            <div className={styles.map + " h-100"}>
                                <Map
                                    google={google}
                                    zoom={18}
                                    style={{
                                        position: "static",
                                        height: "100%",
                                        borderRadius: "5px",
                                    }}
                                    initialCenter={{
                                        lat: contact.siteAddress.latitude,
                                        lng: contact.siteAddress.longitude,
                                    }}
                                    center={mapCenter}
                                >
                                    <Marker name={contact.siteAddress.line1} position={mapCenter} />
                                </Map>
                            </div>
                        )}
                    </div>
                </div>
            </ModalBody>

            <ModalFooter>
                <button type="button" className="btn btn-success btn-sm" onClick={handleSubmitClick} title="Submit the request">Submit</button>
                <button type="button" className="btn btn-danger btn-sm" onClick={handleCancelClick} title="Cancel submitting the request">Cancel</button>
            </ModalFooter>
        </Modal>
    );

}

export default GoogleApiWrapper({
    apiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY
})(RequestService);